/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  CogIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { driftService, DriftMonitor, DriftReport } from '../../services/registryApi';
import toast from 'react-hot-toast';

/* ── helpers ─────────────────────────────────────────────────────────── */

type DriftStatus = 'healthy' | 'warning' | 'drift';

const deriveDriftStatus = (
  report: DriftReport | null,
  threshold: number,
): DriftStatus => {
  if (!report) return 'healthy';
  if (report.drift_detected) return 'drift';
  if (report.overall_score > threshold * 0.7) return 'warning';
  return 'healthy';
};

const statusBadge: Record<
  DriftStatus,
  { label: string; bg: string; text: string }
> = {
  healthy: { label: 'Healthy', bg: 'bg-green-500/20', text: 'text-green-400' },
  warning: { label: 'Warning', bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  drift: { label: 'Drift Detected', bg: 'bg-red-500/20', text: 'text-red-400' },
};

const methodLabels: Record<string, string> = {
  psi: 'PSI',
  ks_test: 'KS Test',
  js_divergence: 'JS Divergence',
};

const parseFeatureScores = (
  raw: string | Record<string, number>,
): Record<string, number> => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw as Record<string, number>;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

/** Map a PSI-like value to a Tailwind background color class. */
const psiCellColor = (value: number): string => {
  if (value >= 0.2) return 'bg-red-500/70';
  if (value >= 0.1) return 'bg-yellow-500/50';
  if (value >= 0.05) return 'bg-yellow-500/25';
  return 'bg-green-500/25';
};

/** Color for a bar in the timeline. */
const barColor = (score: number, threshold: number): string => {
  if (score >= threshold) return 'bg-red-500';
  if (score >= threshold * 0.7) return 'bg-yellow-500';
  return 'bg-green-500';
};

/* ── component ───────────────────────────────────────────────────────── */

const DriftMonitorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showEditConfig, setShowEditConfig] = useState(false);
  const [editForm, setEditForm] = useState({
    threshold: 0.2,
    check_schedule: '',
    sample_size: 1000,
    auto_retrain: false,
    pipeline_id: '',
  });

  /* ---- queries ---- */
  const { data: monitorData, isLoading: monitorLoading } = useQuery({
    queryKey: ['drift-monitor', id],
    queryFn: () => driftService.get(id!).then((r) => r.data),
    enabled: !!id,
  });

  const monitor: DriftMonitor | undefined = monitorData?.drift_monitor;
  const latestReport: DriftReport | null = monitorData?.latest_report ?? null;

  const { data: reportsData, isLoading: reportsLoading } = useQuery({
    queryKey: ['drift-reports', id],
    queryFn: () => driftService.listReports(id!, { limit: 50 }).then((r) => r.data),
    enabled: !!id,
  });

  const reports: DriftReport[] = reportsData?.reports || [];

  /* ---- mutations ---- */
  const triggerCheckMutation = useMutation({
    mutationFn: () => driftService.triggerCheck(id!),
    onSuccess: () => {
      toast.success('Drift check triggered');
      queryClient.invalidateQueries({ queryKey: ['drift-monitor', id] });
      queryClient.invalidateQueries({ queryKey: ['drift-reports', id] });
    },
    onError: () => toast.error('Failed to trigger drift check'),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      driftService.update(id!, {
        threshold: editForm.threshold,
        check_schedule: editForm.check_schedule,
        sample_size: editForm.sample_size,
        auto_retrain: editForm.auto_retrain,
        pipeline_id: editForm.auto_retrain ? editForm.pipeline_id : '',
      } as any),
    onSuccess: () => {
      toast.success('Configuration updated');
      queryClient.invalidateQueries({ queryKey: ['drift-monitor', id] });
      setShowEditConfig(false);
    },
    onError: () => toast.error('Failed to update configuration'),
  });

  /* ---- derived data ---- */
  const driftStatus = monitor
    ? deriveDriftStatus(latestReport, monitor.threshold)
    : 'healthy';
  const badge = statusBadge[driftStatus];

  const features: string[] = useMemo(() => {
    if (!monitor?.features) return [];
    try {
      const parsed = JSON.parse(monitor.features);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return monitor.features.split(',').map((f) => f.trim()).filter(Boolean);
    }
  }, [monitor]);

  // Reports sorted oldest-first for the timeline
  const sortedReports = useMemo(
    () => [...reports].sort((a, b) => new Date(a.checked_at).getTime() - new Date(b.checked_at).getTime()),
    [reports],
  );

  // Max score for chart scaling
  const maxScore = useMemo(() => {
    if (sortedReports.length === 0) return 1;
    const max = Math.max(...sortedReports.map((r) => r.overall_score));
    return Math.max(max, monitor?.threshold ?? 0.2) * 1.2;
  }, [sortedReports, monitor]);

  /* ---- open edit modal with current values ---- */
  const openEditConfig = () => {
    if (!monitor) return;
    setEditForm({
      threshold: monitor.threshold,
      check_schedule: monitor.check_schedule,
      sample_size: monitor.sample_size,
      auto_retrain: monitor.auto_retrain,
      pipeline_id: monitor.pipeline_id || '',
    });
    setShowEditConfig(true);
  };

  /* ---- render ---- */
  if (monitorLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (!monitor) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400">Drift monitor not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back + Header */}
        <button
          onClick={() => navigate('/registry/drift')}
          className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Drift Monitors
        </button>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-white">{monitor.name}</h1>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}
            >
              {badge.label}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={openEditConfig}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg transition-colors"
            >
              <PencilSquareIcon className="w-4 h-4" />
              Edit Config
            </button>
            <button
              onClick={() => triggerCheckMutation.mutate()}
              disabled={triggerCheckMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className="w-4 h-4" />
              {triggerCheckMutation.isPending ? 'Running...' : 'Run Check'}
            </button>
          </div>
        </div>

        {/* Config Card */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
            Configuration
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-gray-500">Method</p>
              <p className="text-sm text-white font-medium">
                {methodLabels[monitor.method] || monitor.method}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Threshold</p>
              <p className="text-sm text-white font-medium">{monitor.threshold}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Schedule</p>
              <p className="text-sm text-white font-mono">{monitor.check_schedule}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Sample Size</p>
              <p className="text-sm text-white font-medium">
                {monitor.sample_size.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Auto Retrain</p>
              <p className="text-sm text-white font-medium">
                {monitor.auto_retrain ? (
                  <span className="text-green-400">Enabled</span>
                ) : (
                  <span className="text-gray-500">Disabled</span>
                )}
              </p>
            </div>
            {monitor.auto_retrain && monitor.pipeline_id && (
              <div>
                <p className="text-xs text-gray-500">Pipeline</p>
                <p className="text-sm text-orange-400 font-mono truncate">
                  {monitor.pipeline_id.slice(0, 12)}...
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Drift Score Timeline */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
            Drift Score Timeline
          </h2>
          {sortedReports.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-500">
              No reports yet. Run a drift check to generate the first report.
            </div>
          ) : (
            <div className="relative h-56">
              {/* Threshold line */}
              <div
                className="absolute left-0 right-0 border-t-2 border-dashed border-red-500/60 z-10"
                style={{
                  bottom: `${(monitor.threshold / maxScore) * 100}%`,
                }}
              >
                <span className="absolute right-0 -top-5 text-xs text-red-400 bg-gray-900 px-1">
                  threshold ({monitor.threshold})
                </span>
              </div>

              {/* Bars */}
              <div className="flex items-end h-full gap-1">
                {sortedReports.map((report) => {
                  const heightPct = Math.max(
                    (report.overall_score / maxScore) * 100,
                    2,
                  );
                  const color = barColor(
                    report.overall_score,
                    monitor.threshold,
                  );
                  return (
                    <div
                      key={report.id}
                      className="flex-1 flex flex-col items-center justify-end h-full group relative"
                    >
                      <div
                        className={`w-full max-w-[40px] rounded-t ${color} transition-all group-hover:opacity-80`}
                        style={{ height: `${heightPct}%` }}
                      />
                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-2 hidden group-hover:block z-20">
                        <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs whitespace-nowrap shadow-lg">
                          <p className="text-white font-medium">
                            Score: {report.overall_score.toFixed(4)}
                          </p>
                          <p className="text-gray-400">
                            {new Date(report.checked_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      {/* Date label */}
                      <p className="text-[10px] text-gray-600 mt-1 truncate max-w-full">
                        {new Date(report.checked_at).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Per-Feature PSI Heatmap */}
        {features.length > 0 && sortedReports.length > 0 && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6 overflow-x-auto">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
              Per-Feature Score Heatmap
            </h2>
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-900">
                    Feature
                  </th>
                  {sortedReports.slice(-12).map((report) => (
                    <th
                      key={report.id}
                      className="px-2 py-2 text-center text-[10px] text-gray-500"
                    >
                      {new Date(report.checked_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((feature) => (
                  <tr key={feature} className="border-t border-gray-800">
                    <td className="px-3 py-2 text-sm text-white font-mono sticky left-0 bg-gray-900">
                      {feature}
                    </td>
                    {sortedReports.slice(-12).map((report) => {
                      const scores = parseFeatureScores(report.feature_scores);
                      const value = scores[feature];
                      const hasValue = value !== undefined && value !== null;
                      return (
                        <td key={report.id} className="px-2 py-2 text-center">
                          {hasValue ? (
                            <span
                              className={`inline-block px-2 py-1 rounded text-xs font-mono text-white ${psiCellColor(value)}`}
                            >
                              {value.toFixed(3)}
                            </span>
                          ) : (
                            <span className="text-gray-600 text-xs">--</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Reports Table */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-800">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
              Reports
            </h2>
          </div>
          {reportsLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
            </div>
          ) : reports.length === 0 ? (
            <div className="p-12 text-center text-gray-500 text-sm">
              No reports available. Run a drift check to generate a report.
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Checked At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Overall Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Drift Detected
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Sample Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                    Action Taken
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                    View
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {reports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-800/40">
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {new Date(report.checked_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-white">
                      {report.overall_score.toFixed(4)}
                    </td>
                    <td className="px-6 py-4">
                      {report.drift_detected ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-red-500/20 text-red-400">
                          <XCircleIcon className="w-3 h-3" />
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-green-500/20 text-green-400">
                          <CheckCircleIcon className="w-3 h-3" />
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {report.sample_count.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {report.action_taken || 'None'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/registry/drift/${id}/reports/${report.id}`}
                        className="text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Edit Config Modal */}
        {showEditConfig && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
              <h2 className="text-xl font-semibold text-white mb-6">
                Edit Configuration
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Threshold
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    value={editForm.threshold}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        threshold: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Check Schedule (cron)
                  </label>
                  <input
                    type="text"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                    value={editForm.check_schedule}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, check_schedule: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    Sample Size
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    value={editForm.sample_size}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        sample_size: parseInt(e.target.value, 10) || 1000,
                      }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-lg px-4 py-3">
                  <p className="text-sm text-white font-medium">Auto Retrain</p>
                  <button
                    type="button"
                    onClick={() =>
                      setEditForm((f) => ({ ...f, auto_retrain: !f.auto_retrain }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      editForm.auto_retrain ? 'bg-orange-500' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        editForm.auto_retrain
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                {editForm.auto_retrain && (
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">
                      Pipeline ID
                    </label>
                    <input
                      type="text"
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                      value={editForm.pipeline_id}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          pipeline_id: e.target.value,
                        }))
                      }
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowEditConfig(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateMutation.mutate()}
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriftMonitorDetail;
